import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tabs, Tab, Badge } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import NotesComponent from "./components/Notes/NotesComponent";
import ExceptionComponent from "./components/Exceptions/ExceptionComponent";
import AlertsComponent from "./components/Alerts/AlertsComponent";
import { Note } from "../../../../../../../api/homeSiteNotes/types";
import { Exception } from "../../../../../../../api/homeSiteExceptions/types";
import { Alert } from "../../../../../../../api/homeSiteAlerts/types";
import { ALERT_ACTIVE_STATUS, ALL_MILESTONES } from "../../../../../../../utils/constants";
import { useDialogBoxContext } from "../../../../../../../components/decisionDialogBox/decisionDialogBoxContext";
import { useCreateNewNote } from "../../../../../../../reactQuery/homeSites/notes/homeSiteNotes";
import { exceptionTypes } from "./components/Exceptions/AddExceptionForm";
import { useCreateExceptions } from "../../../../../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { decisionDialogBoxMessage, decisionDialogBoxTitle } from "../../../../../../../components/decisionDialogBox/decisionDialogBox";
import { DivisionMileStone } from "../../../../../../../api/divisionMileStones/types";

// Extract styles to prevent re-creation on every render
const tabsStyles = {
  display: "flex",
  justifyContent: "space-evenly",
  "& .MuiTabs-indicator": { height: "2px", backgroundColor: "#3F63C0" },
  width: "100%",
};

const tabIndicatorProps = {
  style: {
    top: 0,
    bottom: "unset",
  },
};

const activeTabStyle = {
  flex: 1,
  backgroundColor: "#FFFFFF",
  transition: "background-color 0.3s ease",
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  maxWidth: "unset",
};

const inactiveTabStyle = {
  flex: 1,
  backgroundColor: "#FAFBFC",
  transition: "background-color 0.3s ease",
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  maxWidth: "unset",
};

const tabLabelContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

const tabIconLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

const badgeStyle = {
  marginLeft: "8px",
};

const spanStyle = {
  textTransform: "none" as const,
};

interface AdditionalActionsDetailsProps {
  userId: string;
  homeSiteId: string;
  notesState: Note[];
  exceptionsState: Exception[];
  alertState: Alert[];
  mileStones: DivisionMileStone[];
  tab?: string;
  divisionCode: string;
  conciergeEmail: string;
  opportunityId: string;
  milestoneFilter?: string;
  isDirty?: (dirty: boolean) => void;
  setChildHandlers?: (handlers: {
    save: () => Promise<void>;
    cancel: () => void;
  }) => void;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const AdditionalActionsDetails: React.FC<AdditionalActionsDetailsProps> = ({
  notesState,
  exceptionsState,
  alertState,
  homeSiteId,
  userId,
  mileStones,
  tab,
  divisionCode,
  conciergeEmail,
  opportunityId,
  isDirty,
  setChildHandlers,
  showSnackbar,
  milestoneFilter,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabMapping = useMemo(() => ({ notes: 0, exceptions: 1, alerts: 2, }), []);

  useEffect(() => {
    if (tab && tabMapping[tab as keyof typeof tabMapping] !== undefined) {
      setActiveTab(tabMapping[tab as keyof typeof tabMapping]);
    }
  }, [tab, tabMapping]);

  const { showDecisionDialogBox } = useDialogBoxContext();
  const { mutate: createNote } = useCreateNewNote(divisionCode, homeSiteId);
  const { mutate: createException } = useCreateExceptions(divisionCode, homeSiteId);
  const [noteText, setNoteText] = useState("");
  const [selectedMilestone, setSelectedMilestone] = useState<string>("");
  const [notesDirty, setNotesDirty] = useState(false);

  /* Exception */
  const initialMilestone = milestoneFilter || ALL_MILESTONES;
  const [exceptionName, setExceptionName] = useState("");
  const [description, setDescription] = useState("");
  const [exceptionMilestone, setExceptionMilestone] = useState(initialMilestone);
  const [exceptionType, setExceptionType] = useState<exceptionTypes | null>(null);
  const [exceptionsDirty, setExceptionsDirty] = useState(false);
  const [exceptionUpdateDirty, setExceptionUpdateDirty] = useState(false);
  const [exceptionUpdateHandler, setExceptionUpdateHandler] = useState<null | (() => Promise<void>)>(null);
  const [alertsMilestone, setAlertsMilestone] = useState(initialMilestone);

  const handleSaveNote = useCallback(() => {
    createNote({
      description: noteText,
      divisionCode,
      conciergeEmail,
      homeSiteId,
      mileStoneName: selectedMilestone,
      noteType: "homeSiteNotes",
      createdBy: userId,
      opportunityId,
    });
    setNoteText("");
    setSelectedMilestone("All Milestones");
    setNotesDirty(false);
  }, [conciergeEmail, createNote, divisionCode, homeSiteId, noteText, opportunityId, selectedMilestone, userId]);

  const handleSaveException = useCallback(() => {
    if (
      exceptionName.trim() &&
      description.trim() &&
      exceptionMilestone !== "All Milestones" &&
      exceptionType
    ) {
      createException({
        description,
        homeSiteId,
        mileStoneName: exceptionMilestone,
        divisionCode,
        conciergeEmail,
        exceptionType,
        exceptionName,
        createdBy: userId,
      });
    }

    setExceptionName("");
    setDescription("");
    setExceptionMilestone("All Milestones");
    setExceptionType(null);
    setExceptionsDirty(false);
  }, [conciergeEmail, createException, description, divisionCode, exceptionMilestone, exceptionName, exceptionType, homeSiteId, userId]);

  const handleSaveAll = useCallback(async () => {
    if (notesDirty) {
      handleSaveNote();
    }
    if (exceptionsDirty) {
      handleSaveException();
    }
    if (exceptionUpdateDirty && exceptionUpdateHandler) {
      await exceptionUpdateHandler();
    }
    setNotesDirty(false);
    setExceptionsDirty(false);
    setExceptionUpdateDirty(false);
    setExceptionUpdateHandler(null);
  }, [notesDirty, exceptionsDirty, exceptionUpdateDirty, exceptionUpdateHandler, handleSaveNote, handleSaveException]);

  const handleCancelAll = useCallback(() => {
    setNotesDirty(false);
    setNoteText("");
    setSelectedMilestone("All Milestones");
    setExceptionsDirty(false);
    setExceptionName("");
    setDescription("");
    setExceptionMilestone("All Milestones");
    setExceptionType(null);
  }, []);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    if (notesDirty || exceptionsDirty || exceptionUpdateDirty) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        () => {
          handleSaveAll();
          setActiveTab(newValue);
        },
        () => {
          handleCancelAll();
          setActiveTab(newValue);
        }
      );
    } else {
      setActiveTab(newValue);
    }
  }, [notesDirty, exceptionsDirty, exceptionUpdateDirty, showDecisionDialogBox, handleSaveAll, handleCancelAll]);

  // Compute active counts for Notes, Exceptions, and Alerts
  const activeNotesCount = useMemo(() => notesState?.filter((nt) => nt.status && nt.noteType === "homeSiteNotes").length, [notesState]);

  const activeExceptionsCount = useMemo(() => exceptionsState?.filter((ex) => ex.status === true).length, [exceptionsState]);

  const activeAlertsCount = useMemo(() => alertState?.filter((el) => el.status === ALERT_ACTIVE_STATUS).length, [alertState]);

  // Memoize the renderBadge function to prevent re-renders
  const renderBadge = useCallback((
    count: number,
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning"
  ) => <Badge badgeContent={count} color={color} sx={badgeStyle} max={999} showZero />, []);

  useEffect(() => {
    if (isDirty) {
      isDirty(notesDirty || exceptionsDirty);
    }
  }, [notesDirty, exceptionsDirty, isDirty]);

  useEffect(() => {
    if (setChildHandlers) {
      setChildHandlers({
        save: handleSaveAll,
        cancel: handleCancelAll,
      });
    }
  }, [setChildHandlers, handleSaveAll, handleCancelAll]);

  // Memoize the isDirty callback to prevent child re-renders
  const handleNotesDirty = useCallback((dirty: boolean) => {
    setNotesDirty(dirty);
    isDirty?.(dirty);
  }, [isDirty]);

  // Memoize tab labels to prevent re-creation
  const notesTabLabel = useMemo(() => (
    <Box sx={tabLabelContainerStyle}>
      <Box sx={tabIconLabelStyle}>
        <ChatBubbleOutlineIcon
          sx={{ color: "#3F63C0" }}
          fontSize="small"
        />
        {activeTab === 0 && (
          <span style={spanStyle}>Notes</span>
        )}
      </Box>
      {activeTab === 0 && renderBadge(activeNotesCount, "primary")}
    </Box>
  ), [activeTab, activeNotesCount, renderBadge]);

  const exceptionsTabLabel = useMemo(() => (
    <Box sx={tabLabelContainerStyle}>
      <Box sx={tabIconLabelStyle}>
        <WarningAmberOutlinedIcon
          sx={{ color: "#C93939" }}
          fontSize="small"
        />
        {activeTab === 1 && (
          <span style={spanStyle}>Exceptions</span>
        )}
      </Box>
      {activeTab === 1 && renderBadge(activeExceptionsCount, "error")}
    </Box>
  ), [activeTab, activeExceptionsCount, renderBadge]);

  const alertsTabLabel = useMemo(() => (
    <Box sx={tabLabelContainerStyle}>
      <Box sx={tabIconLabelStyle}>
        <ErrorOutlineOutlinedIcon
          sx={{ color: "#c58026" }}
          fontSize="small"
        />
        {activeTab === 2 && (
          <span style={spanStyle}>Alerts</span>
        )}
      </Box>
      {activeTab === 2 && renderBadge(activeAlertsCount, "warning")}
    </Box>
  ), [activeTab, activeAlertsCount, renderBadge]);

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Home Site Actions Tabs"
        TabIndicatorProps={tabIndicatorProps}
        sx={tabsStyles}
      >
        {/* Notes Tab */}
        <Tab
          sx={activeTab === 0 ? activeTabStyle : inactiveTabStyle}
          label={notesTabLabel}
        />

        {/* Exceptions Tab */}
        <Tab
          sx={activeTab === 1 ? activeTabStyle : inactiveTabStyle}
          label={exceptionsTabLabel}
        />

        {/* Alerts Tab */}
        <Tab
          sx={activeTab === 2 ? activeTabStyle : inactiveTabStyle}
          label={alertsTabLabel}
        />
      </Tabs>

      {/* Tab Panels */}
      <Box>
        {activeTab === 0 && (
          <Box>
            <NotesComponent
              notesState={notesState}
              homeSiteId={homeSiteId}
              userId={userId}
              mileStones={mileStones}
              divisionCode={divisionCode}
              conciergeEmail={conciergeEmail}
              opportunityId={opportunityId}
              noteText={noteText}
              setNoteText={setNoteText}
              selectedMilestone={selectedMilestone || (milestoneFilter ? milestoneFilter : ALL_MILESTONES)}
              setSelectedMilestone={setSelectedMilestone}
              isDirty={handleNotesDirty}
              setChildHandlers={setChildHandlers}
              showSnackbar={showSnackbar}
            />
          </Box>
        )}
        {activeTab === 1 && (
          <Box>
            <ExceptionComponent
              exceptionState={exceptionsState}
              homeSiteId={homeSiteId}
              userId={userId}
              mileStones={mileStones}
              divisionCode={divisionCode}
              conciergeEmail={conciergeEmail}
              opportunityId={opportunityId}
              exceptionName={exceptionName}
              setExceptionName={setExceptionName}
              description={description}
              setDescription={setDescription}
              exceptionMilestone={exceptionMilestone}
              setExceptionMilestone={setExceptionMilestone}
              exceptionType={exceptionType}
              setExceptionType={setExceptionType}
              exceptionsDirty={exceptionsDirty}
              setExceptionsDirty={setExceptionsDirty}
              setExceptionUpdateDirty={setExceptionUpdateDirty}
              setExceptionUpdateHandler={setExceptionUpdateHandler}
              showSnackbar={showSnackbar}
            />
          </Box>
        )}
        {activeTab === 2 && (
          <Box>
            <AlertsComponent
              alertState={alertState}
              divisionCode={divisionCode}
              homeSiteId={homeSiteId}
              userId={userId}
              mileStones={mileStones}
              conciergeEmail={conciergeEmail}
              showSnackbar={showSnackbar}
              selectedMilestone={alertsMilestone}
              setAlertMilestone={setAlertsMilestone}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdditionalActionsDetails;